class VATToggle extends HTMLElement {
  constructor() {
    super();
    this.toggle = this.querySelector('input[type="checkbox"]');
    this.VAT_RATE = 1.2;
    this.STORAGE_KEY = 'vatDisplayMode';
    this.isIncVAT = true;
  }

  connectedCallback() {
    this.loadSavedPreference();

    if (this.toggle) {
      this.toggle.checked = this.isIncVAT;
      this.toggle.addEventListener('change', this.handleToggleChange.bind(this));
    }

    setTimeout(() => this.updateAllPrices(), 50);

    document.addEventListener('quick-view:loaded', this.handleQuickViewLoaded.bind(this));
    this.onCartRefreshed = () => {
      if (this.toggle) this.isIncVAT = this.toggle.checked;
      this.updateAllPrices();
    };
    document.addEventListener('cart:refreshed', this.onCartRefreshed);

    if (window.FoxTheme && window.FoxTheme.pubsub) {
      window.FoxTheme.pubsub.subscribe('variant-change', this.handleVariantChange.bind(this));
      window.FoxTheme.pubsub.subscribe('cart-update', this.handleCartUpdate.bind(this));
    }

    // Watch for DOM changes (e.g., when filters update products)
    this.setupMutationObserver();
  }

  disconnectedCallback() {
    if (this.toggle) {
      this.toggle.removeEventListener('change', this.handleToggleChange.bind(this));
    }
    document.removeEventListener('quick-view:loaded', this.handleQuickViewLoaded.bind(this));
    if (this.onCartRefreshed) {
      document.removeEventListener('cart:refreshed', this.onCartRefreshed);
    }

    if (this.observer) {
      this.observer.disconnect();
    }
  }

  loadSavedPreference() {
    const savedMode = this.getStorage(this.STORAGE_KEY);
    this.isIncVAT = savedMode !== 'ex';
  }

  handleToggleChange(event) {
    this.isIncVAT = event.target.checked;
    this.setStorage(this.STORAGE_KEY, this.isIncVAT ? 'inc' : 'ex');
    this.updateAllPrices();
    this.syncOtherToggles();
  }

  handleVariantChange(event) {
    setTimeout(() => {
      if (this.toggle) {
        this.isIncVAT = this.toggle.checked;
      }

      if (event && event.data && event.data.variant && event.data.sectionId) {
        const { variant, sectionId } = event.data;
        const section = document.querySelector(`#shopify-section-${sectionId}`);

        if (section) {
          const priceElements = section.querySelectorAll('[data-vat-price]');

          priceElements.forEach((priceEl) => {
            if (priceEl.classList.contains('f-price-item--regular') ||
                priceEl.classList.contains('f-price-item--sale')) {
              priceEl.dataset.vatPrice = variant.price;
            }
          });
        }
      }

      this.updateAllPrices();
    }, 50);
  }

  handleCartUpdate(event) {
    setTimeout(() => {
      if (this.toggle) {
        this.isIncVAT = this.toggle.checked;
      }

      // Wait for cart drawer to be rendered
      this.waitForCartDrawer(() => {
        this.updateAllPrices();
      });
    }, 150);
  }

  waitForCartDrawer(callback, attempts = 0) {
    const maxAttempts = 20;
    const cartDrawer = document.querySelector('cart-drawer');

    if (cartDrawer && !cartDrawer.hasAttribute('hidden')) {
      const hasContent = cartDrawer.querySelector('[data-vat-cart-price], [data-vat-total]');
      if (hasContent) {
        callback();
        return;
      }
    }

    if (attempts < maxAttempts) {
      setTimeout(() => this.waitForCartDrawer(callback, attempts + 1), 50);
    } else {
      callback();
    }
  }

  handleQuickViewLoaded() {
    if (this.toggle) {
      this.isIncVAT = this.toggle.checked;
    }
    this.updateAllPrices();
  }

  syncOtherToggles() {
    const allToggles = document.querySelectorAll('vat-toggle input[type="checkbox"]');
    allToggles.forEach(toggle => {
      if (toggle !== this.toggle) {
        toggle.checked = this.isIncVAT;
      }
    });
  }

  setupMutationObserver() {
    // Watch for new products being added to the page (e.g., via filters)
    this.observer = new MutationObserver((mutations) => {
      let shouldUpdate = false;

      mutations.forEach((mutation) => {
        // Check if new nodes with prices were added
        if (mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check if the added node contains price elements or VAT indicators
              if (node.querySelector && (node.querySelector('[data-vat-price]') || node.querySelector('.vat-indicator'))) {
                shouldUpdate = true;
              }
            }
          });
        }
      });

      if (shouldUpdate) {
        // Delay to ensure DOM is fully updated
        setTimeout(() => this.updateAllPrices(), 100);
      }
    });

    // Observe the main content area for changes
    const mainContent = document.querySelector('main') || document.body;
    this.observer.observe(mainContent, {
      childList: true,
      subtree: true
    });
  }

  updateAllPrices() {
    this.updateProductPrices();
    this.updateCartPrices();
    this.updateVATIndicators();
  }

  updateProductPrices() {
    const priceElements = document.querySelectorAll('[data-vat-price]');

    priceElements.forEach((priceEl) => {
      const basePrice = parseFloat(priceEl.dataset.vatPrice);

      if (isNaN(basePrice)) {
        return;
      }

      const displayPrice = this.isIncVAT ? basePrice * this.VAT_RATE : basePrice;
      const formattedPrice = this.formatMoney(displayPrice);

      // Check if this price element has a VAT indicator as a sibling in parent
      const parent = priceEl.closest('.f-price');
      const hasIndicator = parent && parent.querySelector('.vat-indicator');

      if (hasIndicator) {
        // Only update the text content, preserve any child elements
        const textNodes = Array.from(priceEl.childNodes).filter(node => node.nodeType === Node.TEXT_NODE);
        textNodes.forEach(node => node.remove());
        priceEl.insertBefore(document.createTextNode(formattedPrice), priceEl.firstChild);
      } else {
        priceEl.textContent = formattedPrice;
      }
    });
  }

  updateCartPrices() {
    const cartItemPrices = document.querySelectorAll('[data-vat-cart-price]');

    cartItemPrices.forEach((priceEl) => {
      const basePrice = parseFloat(priceEl.dataset.vatCartPrice);

      if (isNaN(basePrice)) return;

      const displayPrice = this.isIncVAT ? basePrice * this.VAT_RATE : basePrice;
      const formattedPrice = this.formatMoney(displayPrice);

      // Preserve VAT indicator if it exists
      const indicator = priceEl.querySelector('.vat-indicator');
      if (indicator) {
        // Update only the text nodes, not the entire content
        const textNodes = Array.from(priceEl.childNodes).filter(node => node.nodeType === Node.TEXT_NODE);
        textNodes.forEach(node => node.remove());
        priceEl.insertBefore(document.createTextNode(formattedPrice), indicator);
      } else {
        priceEl.textContent = formattedPrice;
      }
    });

    const subtotalElement = document.querySelector('[data-vat-subtotal]');
    if (subtotalElement) {
      const baseSubtotal = parseFloat(subtotalElement.dataset.vatSubtotal);

      if (!isNaN(baseSubtotal)) {
        const displaySubtotal = this.isIncVAT ? baseSubtotal * this.VAT_RATE : baseSubtotal;
        const formattedSubtotal = this.formatMoney(displaySubtotal);

        // Preserve VAT indicator if it exists
        const indicator = subtotalElement.querySelector('.vat-indicator');
        if (indicator) {
          const textNodes = Array.from(subtotalElement.childNodes).filter(node => node.nodeType === Node.TEXT_NODE);
          textNodes.forEach(node => node.remove());
          subtotalElement.insertBefore(document.createTextNode(formattedSubtotal), indicator);
        } else {
          subtotalElement.textContent = formattedSubtotal;
        }
      }
    }

    const totalElement = document.querySelector('[data-vat-total]');
    if (totalElement) {
      const baseTotal = parseFloat(totalElement.dataset.vatTotal);

      if (!isNaN(baseTotal)) {
        const displayTotal = this.isIncVAT ? baseTotal * this.VAT_RATE : baseTotal;
        const formattedTotal = this.formatMoney(displayTotal);

        // Preserve VAT indicator if it exists
        const indicator = totalElement.querySelector('.vat-indicator');
        if (indicator) {
          const textNodes = Array.from(totalElement.childNodes).filter(node => node.nodeType === Node.TEXT_NODE);
          textNodes.forEach(node => node.remove());
          totalElement.insertBefore(document.createTextNode(formattedTotal), indicator);
        } else {
          totalElement.textContent = formattedTotal;
        }
      }
    }
  }

  updateVATIndicators() {
    const indicators = document.querySelectorAll('.vat-indicator');
    const indicatorText = this.isIncVAT ? 'Inc VAT' : 'Ex VAT';

    indicators.forEach((indicator) => {
      indicator.textContent = indicatorText;
    });
  }

  formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, window.FoxTheme?.settings?.moneyFormat);
    }

    const amount = (cents / 100).toFixed(2);
    return `£${amount}`;
  }

  getStorage(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  setStorage(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (e) {
    }
  }
}

if (!customElements.get('vat-toggle')) {
  customElements.define('vat-toggle', VATToggle);
}

document.addEventListener('DOMContentLoaded', () => {
  const toggles = document.querySelectorAll('vat-toggle');
  if (toggles.length > 0) {
    setTimeout(() => {
      toggles[0].dispatchEvent(new Event('load'));
    }, 100);
  }
});
